import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import BrandLogo from '@/components/BrandLogo'
import { formatPrice as formatAmount, productTitle, roundCents } from '@/lib/format'
import { useTheme } from '@/lib/hooks'
import i18n from '@/lib/i18n'
import { onStateUpdate } from '@/lib/broadcastSync'

import {
  Amount,
  CartItemInfo,
  CartItemName,
  CartItemQty,
  CartItemRow,
  CartItemTotal,
  CartLayout,
  CenterContent,
  Currency,
  DiscountAmount,
  Headline,
  HintPill,
  IconBadge,
  ItemCount,
  ItemsHeader,
  ItemsList,
  ItemsTitle,
  LastItemContent,
  LastItemImage,
  LastItemName,
  LastItemPanel,
  LastItemPrice,
  LogoBar,
  LoyaltyLabel,
  LoyaltyRow,
  PaidAmount,
  Panel,
  ProcessingRing,
  SideColumn,
  Subline,
  SuccessBadge,
  SummaryPanel,
  Thumb,
  TotalAmount,
  TotalBlock,
  TotalLabel,
  Wrapper
} from './CustomerDisplay.styles'

const log = (tag, ...args) => {
  console.log(`[CUSTOMER_DISPLAY][${tag}]`, new Date().toISOString(), ...args)
}

// "£12.30" -> £ + 12.30, "1 186,87 zł" -> 1 186,87 + zł (split on the last space),
// so the currency can be set smaller than the number
const splitCurrency = (text) => {
  const suffix = text.match(/^(.*\d)[\s\u00A0]+(\D+)$/)
  if (suffix) return { prefix: '', number: suffix[1], suffix: suffix[2] }
  const prefix = text.match(/^(\D+?)\s*(\d.*)$/)
  if (prefix) return { prefix: prefix[1], number: prefix[2], suffix: '' }
  return { prefix: '', number: text, suffix: '' }
}

const CustomerDisplay = () => {
  const { t } = useTranslation()
  const themes = useTheme()
  const [cart, setCart] = useState([])
  const [loyaltyCard, setLoyaltyCard] = useState(null)
  const [currentModal, setCurrentModal] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState('DEFAULT')
  const [lastScannedId, setLastScannedId] = useState(null)

  useEffect(() => {
    log('INIT', 'CustomerDisplay mounted')
    log('INIT', `Viewport: ${window.innerWidth}x${window.innerHeight} DPR:${window.devicePixelRatio}`)
    log('INIT', `Screen: ${window.screen.width}x${window.screen.height}`)
    log('INIT', `Orientation: ${window.screen.orientation?.type || 'unknown'}`)
    log('INIT', `UserAgent: ${navigator.userAgent}`)
    log('INIT', `AndroidBridge available: ${!!window.AndroidBridge}`)
    log('INIT', `BroadcastChannel available: ${typeof BroadcastChannel !== 'undefined'}`)

    let updateCount = 0
    const applyState = (state) => {
      updateCount++
      const cartSummary = state.cart?.length
        ? `${state.cart.length} items, total=${state.cart.reduce((s, i) => s + i.qty * i.price, 0).toFixed(2)}`
        : 'empty'
      log('UPDATE', `#${updateCount} cart=[${cartSummary}] modal=${state.currentModal} payment=${state.paymentStatus}`)
      setCart(state.cart || [])
      setLoyaltyCard(state.loyaltyCard || null)
      setCurrentModal(state.currentModal || null)
      setPaymentMethod(state.paymentMethod || null)
      setPaymentStatus(state.paymentStatus || 'DEFAULT')
      setLastScannedId(state.lastScannedProduct?.id ?? null)
      if (state.language && state.language !== i18n.language) {
        i18n.changeLanguage(state.language)
      }
    }

    // BroadcastChannel (works in desktop browser, same-origin tabs)
    const cleanup = onStateUpdate(applyState)

    // Android native bridge: state is pushed via window.__updateCustomerState
    window.__updateCustomerState = (jsonString) => {
      log('ANDROID_BRIDGE', `Received __updateCustomerState (${typeof jsonString === 'string' ? jsonString.length + ' bytes' : 'object'})`)
      try {
        const state = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
        applyState(state)
      } catch (e) {
        log('ANDROID_BRIDGE', 'Parse ERROR:', e.message)
      }
    }

    return () => {
      log('CLEANUP', 'CustomerDisplay unmounting')
      cleanup()
      delete window.__updateCustomerState
    }
  }, [])

  const cartTotal = cart.reduce((sum, item) => sum + item.qty * item.price, 0)
  // Same rounding as the cashier: gross - discount = total, to the cent
  const finalTotal = roundCents(loyaltyCard ? cartTotal - cartTotal * 0.10 : cartTotal)
  const discount = loyaltyCard ? roundCents(roundCents(cartTotal) - finalTotal) : 0
  const itemCount = cart.reduce((sum, item) => sum + item.qty, 0)
  // Latest scanned product, resolved through the cart (active brand's catalogue); fallback: last cart line
  const lastItem = cart.find(item => item.id === lastScannedId) || cart[cart.length - 1]

  // Keep the newest item in view as the cashier scans
  const listRef = useRef(null)
  useEffect(() => {
    const list = listRef.current
    if (list) list.scrollTo({ top: list.scrollHeight })
  }, [cart.length])

  const formatPrice = (amount) => formatAmount(amount, themes.currency)
  const isCash = paymentMethod === 'CASH'
  const totalParts = splitCurrency(formatPrice(finalTotal))

  // Payment processing screen
  if (currentModal === 'PAYMENT' && paymentStatus === 'PROCESSING') {
    return (
      <Wrapper>
        <LogoBar>
          <BrandLogo size={48} />
        </LogoBar>
        <CenterContent>
          <IconBadge>
            <ProcessingRing />
            {isCash ? <CashIcon /> : <CardIcon />}
          </IconBadge>
          <Headline as="h2">
            {isCash ? t('customerDisplay.cashPayment') : t('customerDisplay.cardPayment')}
          </Headline>
          <Amount>{formatPrice(finalTotal)}</Amount>
          <HintPill>
            {isCash ? <CashIcon /> : <ContactlessIcon />}
            <span>{isCash ? t('customerDisplay.handCash') : t('customerDisplay.tapCard')}</span>
          </HintPill>
        </CenterContent>
      </Wrapper>
    )
  }

  // Payment complete screen
  if (currentModal === 'CONFIRMATION') {
    return (
      <Wrapper>
        <LogoBar>
          <BrandLogo size={48} />
        </LogoBar>
        <CenterContent>
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            <SuccessBadge>
              <svg viewBox="0 0 52 52">
                <motion.path
                  d="M14 27 l7 7 17-17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                />
              </svg>
            </SuccessBadge>
          </motion.div>
          <Headline>{t('customerDisplay.thankYou')}</Headline>
          <Subline>{t('customerDisplay.complete')}</Subline>
          <PaidAmount>{formatPrice(finalTotal)}</PaidAmount>
        </CenterContent>
      </Wrapper>
    )
  }

  // Default: show cart
  return (
    <Wrapper>
      <LogoBar>
        <BrandLogo size={48} />
      </LogoBar>

      {cart.length === 0 ? (
        <CenterContent>
          <IconBadge>
            <BagIcon />
          </IconBadge>
          <Headline>{t('customerDisplay.welcome')}</Headline>
          <Subline>{t('customerDisplay.itemsAppear')}</Subline>
        </CenterContent>
      ) : (
        <CartLayout>
          <Panel>
            <ItemsHeader>
              <ItemsTitle>{t('customerDisplay.yourItems')}</ItemsTitle>
              <ItemCount>{t('customerDisplay.itemCount', { count: itemCount })}</ItemCount>
            </ItemsHeader>

            <ItemsList ref={listRef}>
              <AnimatePresence initial={false}>
                {cart.map(item => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <CartItemRow>
                      {item.imageUrl && (
                        <Thumb>
                          <img src={item.imageUrl} alt="" />
                        </Thumb>
                      )}
                      <CartItemInfo>
                        <CartItemName>{productTitle(item, i18n.language)}</CartItemName>
                        <CartItemQty>{item.qty} × {formatPrice(item.price)}</CartItemQty>
                      </CartItemInfo>
                      <CartItemTotal>{formatPrice(item.qty * item.price)}</CartItemTotal>
                    </CartItemRow>
                  </motion.div>
                ))}
              </AnimatePresence>
            </ItemsList>
          </Panel>

          <SideColumn>
            {/* Latest scanned product, shown large */}
            <LastItemPanel>
              <AnimatePresence mode="wait" initial={false}>
                <LastItemContent
                  key={lastItem.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {lastItem.imageUrl && (
                    <LastItemImage>
                      <img src={lastItem.imageUrl} alt="" />
                    </LastItemImage>
                  )}
                  <LastItemName>{productTitle(lastItem, i18n.language)}</LastItemName>
                  <LastItemPrice>{lastItem.qty} × {formatPrice(lastItem.price)}</LastItemPrice>
                </LastItemContent>
              </AnimatePresence>
            </LastItemPanel>

            <SummaryPanel as="aside">
              {loyaltyCard && (
                <LoyaltyRow>
                  <LoyaltyLabel>{t('customerDisplay.loyaltyDiscount')}</LoyaltyLabel>
                  <DiscountAmount>−{formatPrice(discount)}</DiscountAmount>
                </LoyaltyRow>
              )}
              <TotalBlock $divider={!!loyaltyCard}>
                <TotalLabel>{t('customerDisplay.total')}</TotalLabel>
                <TotalAmount>
                {totalParts.prefix && <Currency>{totalParts.prefix}</Currency>}
                {totalParts.number}
                {totalParts.suffix && <Currency>{`\u00A0${totalParts.suffix}`}</Currency>}
              </TotalAmount>
              </TotalBlock>
            </SummaryPanel>
          </SideColumn>
        </CartLayout>
      )}
    </Wrapper>
  )
}

// Line icons (inherit currentColor)

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true
}

const BagIcon = () => (
  <svg {...iconProps}>
    <path d="M5 8h14l-1.2 11.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 8z" />
    <path d="M9 10V6.5a3 3 0 0 1 6 0V10" />
  </svg>
)

const CardIcon = () => (
  <svg {...iconProps}>
    <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
    <path d="M2.5 10h19M6.5 15h4" />
  </svg>
)

const CashIcon = () => (
  <svg {...iconProps}>
    <rect x="2.5" y="6.5" width="19" height="11" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 9.5v5M18 9.5v5" />
  </svg>
)

const ContactlessIcon = () => (
  <svg {...iconProps}>
    <path d="M8 8.5a5 5 0 0 1 0 7M11.5 6a8.5 8.5 0 0 1 0 12M15 3.5a12 12 0 0 1 0 17" />
  </svg>
)

export default CustomerDisplay
